import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { DrugResMock, DrugSearchResMock } from '../types/drug.type'
import { GeneralResMock } from '../types/general.type'

type Props = {
  id?: string
  page?: number
  limit?: number
  query?: string
  form?: string
}
export const useDrugs = (arg?: Props) => {
  const id = arg?.id
  const query = arg?.query
  const limit = arg?.limit || 10
  const page = arg?.page || 1
  const form = arg?.form
  const { data: drugs, isLoading: drugsIsLoading } = useQuery({
    queryFn: async () => {
      let endpoint = 'drugs?'
      if (page) endpoint += `page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}&`
      if (query) endpoint += `query=${query}&`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<DrugResMock[]>>(endpoint)
      return data
    },
    queryKey: ['drugs', { id, page, query }]
  })
  const { data: drugSearch } = useQuery({
    queryFn: async () => {
      let endpoint = 'drugs/search?'
      if (query) endpoint += `query=${query}&`
      if (form) endpoint += `form=${form}&`
      const { data } = await AXIOS_INSTANCE.get(endpoint)
      return data.data
    },
    queryKey: ['drug_search', { query, form }]
  })

  const { data: singleDrug, isLoading: singleDrugIsLoading } = useQuery<GeneralResMock<DrugResMock>>({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get(`drugs/${id}`)
      return data
    },
    queryKey: ['drugs', id]
  })

  const { mutateAsync: createDrug } = useMutation({
    mutationFn: async (formRec: any) => {
      const { data } = await AXIOS_INSTANCE.post('drugs', formRec, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drugs'] })
      queryClient.invalidateQueries({ queryKey: ['drug_search'] })
    }
  })

  const { mutateAsync: updateDrug } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`drugs/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drugs'] })
      queryClient.invalidateQueries({ queryKey: ['drug_search'] })
    }
  })

  const { mutateAsync: deleteDrug } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`drugs/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drugs'] })
      queryClient.invalidateQueries({ queryKey: ['drug_search'] })
    }
  })

  return {
    createDrug,
    deleteDrug,
    drugPagination: drugs?.meta.pagination,
    drugs: drugs?.data,
    drugsIsLoading,
    singleDrug,
    singleDrugIsLoading,
    updateDrug,
    drugSearch: drugSearch as DrugSearchResMock[]
  }
}
